import {
  getCityInfoByName,
  getCityInfoByNameArr,
  getCityInfoByNameWishPower,
} from '@/pages/chargermap/utils/gis'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import mitt, { Emitter } from 'mitt'
import Tracker from '@nio-fe/nio-tracker-web'
import { defineStore } from 'pinia'
import { getIndicatorSummary, getLocation, getSummary } from '../../apis/map.api'
import { IBackList } from '../../components/backListRecord/backList'
import { ChargeMapContext } from '../../components/chargerMap/index.model'
import { FilterItem } from '../../components/filter/filter.model'
import { fetchParams, isFilterShowPowerSwap } from '../../components/filter/filterUtils'
import { SummaryModel } from '../../components/informationComps/information/information.model'
import { IndicatorItemModel } from '../../components/informationComps/soInformation/indicator'
import { NewStationItemModel, TimeDimItem } from '../../components/newStation/newStation.model'
import {
  BubbleDestPoint,
  CityInfo,
  ConfigItem,
  MapViewMode,
  Point,
  PowerRoute,
} from '../../contract/map'
import { SearchPoint } from '../../contract/searchPoint'
import { LocationModel } from '../../models/location.model'
import { useAppStore } from '../../stores/app'
import {
  CountyLayoutConfig,
  CountyLayoutConfigItem,
} from '../../components/countyLayout/countyLayout.model'
import { OutletsModel } from '../../models/outlets.model'
import { ResourceItemModel } from '../../components/iconRender/icon.model'
import { formatDate } from '@/utils/time'

/**
 * 模块之间的通信
 * 比如filter search等，可以给除了nsh5之外的模块使用
 */
export interface ComponentState {
  /**
   * 地图
   */
  mapContext?: ChargeMapContext
  /**
   * 触发bus事件的标识， 所有单个组装功能模块的标识都应该以此为前缀
   */
  bus: Emitter<any>
  /**
   * 筛选条件
   */
  filter: FilterItem[]
  /**
   * 筛选变化
   */
  filterChanged: boolean
  /**
   * 缓存请求的筛选数据筛选变化
   */
  filterRawData: string
  /**
   * 筛选名称
   */
  filterNameMap: { [key: string]: string }
  /**
   * 地图视图模式
   */
  viewMode: MapViewMode
  /**
   * 路径规划终点位置 或者 气泡位置
   */
  bubbleDestLocation?: BubbleDestPoint
  /**
   * 应用配置
   */
  dictionaries: ConfigItem[]
  /**
   * 轮播指示index
   */
  swipeIndex?: number
  /**
   * 实时换电数据
   */
  swapNum?: string | number
  /**
   * 实时充电数据
   */
  chargeNum?: string | number
  /**
   * 选中的城市
   */
  city?: CityInfo
  /**
   * 地址选择器中选中的城市
   */
  selectedCityFromAddressPod?: CityInfo
  // /**
  //  * 资源数据概览
  //  */
  // summary?: SummaryModel
  /**
   * 资源数据概览V2 -- 通过指标处理
   */
  summaryV2?: { [key: string]: any }
  /**
   * 我的位置
   */
  myLocation?: LocationModel
  /**
   * 记录路径规划之后回退的位置
   */
  backPath?: string
  /**
   * 选择的省市区县
   */
  dimension?: string
  /**
   * 是否展示电区房覆盖率
   */
  showPsDistrictHousingCoveredRate?: boolean
  /**
   * 地图中心位置
   */
  centerLocation?: LocationModel
  /**
   * 地图位置缓存
   */
  positionCacheList: any[]
  /**
   * 推荐路线
   */
  recommendRoutes: PowerRoute[]
  /**
   * 全国下的所有资源
   */
  allResources: any
  /**
   * 心愿站-资源分布数据，画heatmap
   */
  wishPowerResources: Point[]
  /**
   * 心愿站-选中的城市
   */
  cityWishPower?: CityInfo
  /**
   * 搜索选择暂存
   */
  tempSearchPoiResource: SearchPoint | null
  /**
   * mobile搜索选择暂存
   */
  searchKeywordInMobile: string | null
  /**
   * 回退路径
   */
  backList: IBackList[]
  /**
   * 筛选看板 开关
   */
  filterPanel: boolean
  /**
   * 搜索列表看板 开关
   */
  searchListPanel: boolean
  /**
   * 详情看板 开关
   */
  detailPanel: boolean
  /**
   * 详情看板控制关闭行为
   */
  cardLoaded: boolean
  /**
   * 是否展示数据概览
   */
  showSummary: boolean
  /**
   * 是否展示操作区域
   */
  showHandleBox: boolean
  /**
   * 卡位等级  0: 低卡位 1: 中卡位  2: 高卡位
   */
  popupDragLevel: number
  /**
   * 是否展示搜索态
   */
  showSearchPanel: boolean
  /**
   * 是否展示搜索背景（和搜索列表是否存在绑定）
   */
  showSearchBackGround: boolean
  /**
   * 是否展示手机号Popover
   */
  showPhonePopover: boolean
  /**
   * 新站上新列表
   */
  newStationList: NewStationItemModel[]
  /**
   * 新站上新列表上方时间维度
   */
  timeDimList: TimeDimItem[]
  /**
   * 县县通配置
   */
  countyLayoutConfig: CountyLayoutConfig
  /**
   * 县县通图层
   */
  countyLayoutMvt: any
  /**
   * 县县通图层 省
   */
  countyLayoutMvtProvince: any
  /**
   * 县县通图层 省
   */
  dataSource: OutletsModel | ResourceItemModel | null
  /**
   * 是否展示未读提示红点
   */
  showCountyLayoutRedDot: boolean
  /**
   * 统计更新时间戳
   */
  statisticUpdateTimestamp: number
  /**
   * 统计更新时间戳
   */
  statisticUpdateTimeString: string
  /**
   * 数字动画背景是否已初始化
   */
  numberAnimationInitialized: boolean
}

export interface ComponentActions {
  getFetchFilterParams: () => any
  getPowerSwapInFilter: () => boolean
  selectCityPure: (name: string) => void
  setMyLocation: (loc: Point) => Promise<LocationModel | undefined>
  setDestLocationByPin: (bubbleInfo: BubbleDestPoint) => void
  unsetDestLocationByPin: () => void
  selectCityByArr: (names: string[]) => void
  // getSummary: (type?: string) => void
  getSummaryV2: (dim_value?: string) => void
  setCenter: (center: Point) => void
  setCachePosition: (id: string) => void
  removeCachePosition: () => any
  clearCachePosition: () => void
  getWishPowerByCND: (cdnUrlRes: any) => void
  selectCityWishPower: (name: string) => void
  triggerPanel: (panel?: string) => void
  clearStatisticUpdateTimer: () => void
  setStatisticUpdateTimestamp: (init?: boolean) => void
}

let retryNumSummary = 0
let statisticUpdateTimer = null as any
export const useComponentStore = defineStore('nsh5_component', {
  state: (): ComponentState => ({
    mapContext: undefined,
    bus: mitt(),
    filter: [],
    filterChanged: false,
    filterRawData: '',
    filterNameMap: {},
    viewMode: MapViewMode.Init,
    bubbleDestLocation: undefined,
    dictionaries: [],
    swipeIndex: 0,
    swapNum: undefined,
    chargeNum: undefined,
    city: getCityInfoByName('全国'),
    selectedCityFromAddressPod: {} as CityInfo,
    // summary: undefined,
    summaryV2: {},
    myLocation: undefined,
    backPath: undefined,
    dimension: '',
    showPsDistrictHousingCoveredRate: true,
    centerLocation: undefined,
    positionCacheList: [],
    recommendRoutes: [],
    allResources: {},
    wishPowerResources: [],
    cityWishPower: getCityInfoByName('全国'),
    tempSearchPoiResource: null,
    searchKeywordInMobile: null,
    backList: [],
    filterPanel: false,
    searchListPanel: false,
    detailPanel: false,
    cardLoaded: false,
    showSummary: false,
    showHandleBox: false,
    popupDragLevel: 1,
    showSearchPanel: false,
    showSearchBackGround: false,
    showPhonePopover: false,
    newStationList: [],
    timeDimList: [],
    countyLayoutConfig: {
      countyLayoutConfigList: [
        {
          itemCode: 'swap_station',
          itemDesc: '换电站',
        },
        {
          itemCode: 'charge_station',
          itemDesc: '充电桩',
        },
      ],
      currentIndex: 0,
    },
    countyLayoutMvt: null,
    countyLayoutMvtProvince: null,
    dataSource: null,
    showCountyLayoutRedDot: false,
    statisticUpdateTimestamp: new Date().getTime(),
    statisticUpdateTimeString: '',
    numberAnimationInitialized: false,
  }),
  actions: {
    /**
     * filter处理，用于传参数
     * @returns
     */
    getFetchFilterParams() {
      return fetchParams(this.filter)
    },
    /**
     * 判断换电站是否在筛选中展示
     * @returns
     */
    getPowerSwapInFilter() {
      return isFilterShowPowerSwap(this.filter)
    },
    /**
     * 选择资源数据概览城市，解耦统计数据
     * @param {string} name 城市名称
     */
    selectCityPure(name: string) {
      const info = getCityInfoByName(name)
      if (!info) {
        return
      }
      this.city = info
    },
    /**
     * 设置我的位置
     * @param {Point} loc 位置
     */
    async setMyLocation(loc: Point) {
      const res = await getLocation(loc, 'setMyLocation')
      if (res.isValid()) {
        this.myLocation = res.data
        return res.data
      }
    },
    /**
     * 设置目的地，显示小气泡
     */
    setDestLocationByPin(bubbleInfo: BubbleDestPoint) {
      this.bubbleDestLocation = bubbleInfo
    },
    /**
     * 设置目的地，显示小气泡
     */
    unsetDestLocationByPin() {
      this.bubbleDestLocation = undefined
    },
    /**
     * 选择资源数据概览城市
     * @param {string} names [省，市，区县]
     */
    selectCityByArr(names: string[]) {
      if (names[0] == '中国区域' && this.city) {
        const cityTemp = JSON.parse(JSON.stringify(this.city))
        cityTemp.name = '中国区域'
        if (this.viewMode === MapViewMode.CountyLayout) {
          cityTemp.code = '990000'
        } else {
          cityTemp.code = '000000'
        }
        this.city = cityTemp
        if (this.viewMode === MapViewMode.Init) {
          // 20250417 只有init模式下才会请求数据概览
          // ;(process.config as any).brand === 'alps' || (process.config as any).brand === 'fy'
          //   ? this.getSummaryV2()
          //   : this.getSummary()
          this.getSummaryV2()
        }
      } else if (names[0] == undefined && this.city) {
        const cityTemp = JSON.parse(JSON.stringify(this.city))
        cityTemp.name = '其他区域'
        this.city = cityTemp
        // this.summary = new SummaryModel()
        this.summaryV2 = new SummaryModel()
      } else {
        let info: CityInfo | undefined
        if (this.dimension) {
          info = this.selectedCityFromAddressPod
        } else {
          info = getCityInfoByNameArr(names)
        }
        if (!info) {
          return
        }
        this.city = info
        console.log('===================================')
        console.log('选中的城市', this.city)
        console.log('====================================')
        if (this.viewMode === MapViewMode.Init) {
          // 20250417 只有init模式下才会请求数据概览
          // ;(process.config as any).brand === 'alps' || (process.config as any).brand === 'fy'
          //   ? this.getSummaryV2(this.city?.code)
          //   : this.getSummary()
          this.getSummaryV2(this.city?.code)
        }
      }
    },
    /**
     * 获取资源数据概览
     */
    // async getSummary(type = 'region') {
    // const res = await getSummary(this.city?.code, this.city?.loc.lng, this.city?.loc.lat, type)
    // if (res.isValid()) {
    //   this.summary = res.data
    // } else {
    //   // 重试
    //   if (retryNumSummary < 3) {
    //     // 重试，1-5秒内随机时间重试
    //     const randomTime = Math.round(Math.random() * 5 * 1000)
    //     setTimeout(() => {
    //       // 重试执行 getComponentsConfig方法
    //       retryNumSummary++
    //       this.getSummary()
    //     }, randomTime)
    //   }
    // }
    // },

    /**
     * 获取资源数据概览
     */
    async getSummaryV2(dim_value = '000000') {
      const STATIC_INDICATOR_CODE_LIST = [
        // 'swap_station_num_for_com', // 换电站数量
        // 'super_charger_station_num', // 超充场站数量
        // 'super_charger_num', // 超充桩数量
        // 'destination_charger_station_num', // 目充场站数量
        // 'destination_charger_num', // 目充桩数量
        // 'public_charger_num', // 公共桩数量
        // 'ps_3km_covered_rate', // 电区房覆盖率
        // 'intercity_swap_station_num', // 高速公路换电站数量
        'power_swap_charge_device_num_total', // 蔚来能源充换电站总数
        'swap_station_num_for_com', // 换电站数量
        'intercity_swap_station_num', // 高速换电站数量
        'power_charge_station_device_num_total', // 充电站数量
        'power_charge_device_num_total', // 充电枪数量
        'public_charger_num', // 接入第三方充电桩
        'charger_other_brand_energy_rate', // 蔚来能源充电桩电量第三方用户占比
      ]
      const indicators = STATIC_INDICATOR_CODE_LIST.map(
        (a) => a + (process.config as any).indicator_suffix
      )
      const res = await getIndicatorSummary(indicators, '', 'region_code', dim_value)
      if (res.isValid()) {
        this.summaryV2 = res.data as { [key: string]: IndicatorItemModel }
      } else {
        // 重试
        if (retryNumSummary < 3) {
          // 重试，1-5秒内随机时间重试
          const randomTime = Math.round(Math.random() * 5 * 1000)
          setTimeout(() => {
            // 重试执行 getComponentsConfig方法
            retryNumSummary++
            this.getSummaryV2()
          }, randomTime)
        }
      }
    },

    /**
     * 根据屏幕中心获取位置信息
     * @param center
     */
    async setCenter(center: Point) {
      const res = await getLocation(center, 'setCenter')
      if (res.isValid()) {
        this.centerLocation = res.data
      }
    },
    /**
     * 缓存位置
     */
    async setCachePosition() {
      const centerCache = this.mapContext!.getCenter() as Point
      const zoomLevelCache = this.mapContext!.getZoom()
      this.positionCacheList.push({
        centerCache,
        zoomLevelCache,
      })
    },
    /**
     * 移除缓存位置
     */
    async removeCachePosition() {
      return this.positionCacheList.pop()
    },
    /**
     * 移除缓存位置
     */
    async clearCachePosition() {
      this.positionCacheList = []
    },
    /**
     * 获取心愿站 heatmap
     */
    async getWishPowerByCND(cdnUrlRes: any) {
      this.wishPowerResources = []
      if (cdnUrlRes['wishPowerLink']) {
        return Promise.all(
          cdnUrlRes['wishPowerLink'].map((url: string) =>
            window
              .fetch(url)
              .then((response) => response.json())
              .then((json) => {
                const res = json.map((loc: string) => {
                  const position = loc.split(',')
                  return gcj02ToBd09({
                    lng: Number(position[0]),
                    lat: Number(position[1]),
                  })
                })
                this.wishPowerResources.push(...(res as Point[]))
                return res
              })
          )
        )
      }
    },
    /**
     * 选择资源数据概览城市
     * @param {string} name 城市名称
     */
    selectCityWishPower(name: string) {
      const info = getCityInfoByNameWishPower(name)
      if (!info) {
        return
      }
      this.cityWishPower = info
    },
    /**
     * 操作看板
     * @param panel
     */
    triggerPanel(panel?: string) {
      switch (panel) {
        case 'filterPanel':
          this.filterPanel = true
          this.searchListPanel = false
          this.detailPanel = false
          break
        case 'searchListPanel':
          this.filterPanel = false
          this.searchListPanel = true
          this.detailPanel = false
          break
        case 'detailPanel':
          this.filterPanel = false
          this.searchListPanel = false
          this.detailPanel = true
          break

        default:
          this.filterPanel = false
          this.searchListPanel = false
          this.detailPanel = false
          break
      }
    },

    clearStatisticUpdateTimer() {
      if (statisticUpdateTimer) {
        clearTimeout(statisticUpdateTimer)
        statisticUpdateTimer = null
      }
    },

    setStatisticUpdateTimestamp(init = true) {
      if (!statisticUpdateTimer && !init) {
        return
      }
      this.clearStatisticUpdateTimer()
      this.statisticUpdateTimestamp = new Date().getTime()
      const { y, MM, d, h, m, s } = formatDate(this.statisticUpdateTimestamp) // 获取当前时间
      this.statisticUpdateTimeString = `${y}.${MM}.${d} ${h}:${m}:${s}`
      statisticUpdateTimer = setInterval(() => {
        this.statisticUpdateTimestamp = new Date().getTime()
        const { y, MM, d, h, m, s } = formatDate(this.statisticUpdateTimestamp) // 获取当前时间
        this.statisticUpdateTimeString = `${y}.${MM}.${d} ${h}:${m}:${s}`
      }, 1000)
    }
  },
})
